import { useEffect } from "react";
import { useStore } from "../../../store/store";
import { IQuestions } from "../useGetQuestions";
import {
	IAuditorInvitation,
	IEvent,
	IPoll,
	IPollVoteEvent,
	IPong,
	IReactionEvent,
	SocketEvents,
} from "./types";
import i18n from "i18next";
import toast from "react-hot-toast";
import { fetchPublicLiveFiles } from "../../api/liveFiles";
import type { ILiveFile } from "../../types/liveFile";
import QuickLRU from "quick-lru";
import { generateSignedUrl, getOrCreateSubscriberId } from "../../../api/generateSignedUrl";
import useSetParticipated from "../../../hooks/useSetParticipated";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IQuestionLike } from "../useLikeQuestion";
import { IMessage } from "../messages/types";
import useSendVerificationEmail from "../useSendVerificationEmail";
import { IStream } from "../../store/types";

const useSocket = (liveId: number | undefined) => {
	const navigate = useNavigate();
	const { liveToken } = useParams();
	const setWebSocket = useStore((state) => state.setWebSocket);
	const setWebSocketError = useStore((state) => state.setWebSocketError);
	const addQuestions = useStore((state) => state.addQuestions);
	const addApprovedQuestions = useStore((state) => state.addApprovedQuestions);
	const setLiveActive = useStore((state) => state.setLiveActive);
	const setLiveStream = useStore((state) => state.setLiveStream);
	const setStatus = useStore((state) => state.setStatus);
	const deleteQuestion = useStore((state) => state.deleteQuestion);
	const manageQuestion = useStore((state) => state.manageQuestion);
	const deleteQuestionByModerator = useStore((state) => state.deleteQuestionByModerator);
	const addAnswers = useStore((state) => state.addAnswers);
	const addMessages = useStore((state) => state.addMessages);
	const addReaction = useStore((state) => state.addReaction);
	const addQuestionLike = useStore((state) => state.addQuestionLike);
	const user = useStore((state) => state.user);
	const isCommentValidation = useStore((state) => state.isCommentValidation);
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const userToken = queryParams.get("token");
	const setParticipated = useSetParticipated(userToken);
	const editQuestion = useStore((state) => state.editQuestion);
	const setBroadcastedQuestion = useStore((state) => state.setBroadcastedQuestion);
	const setUserAlreadyConnected = useStore((state) => state.setUserAlreadyConnected);
	const sendEmailMutation = useSendVerificationEmail(false);
	const setUserSocketId = useStore((state) => state.setUserSocketId);
	const userIpAddress = useStore((state) => state.userIpAddress);
	const addPublicPoll = useStore((state) => state.addPublicPoll);
	const removePublicPoll = useStore((state) => state.removePublicPoll);
	const updatePublicPoll = useStore((state) => state.updatePublicPoll);
	const updatePollVote = useStore((state) => state.updatePollVote);
	const setAuditorInvitation = useStore((state) => state.setAuditorInvitation);

	// Event Handlers
	const handleQuestionEvent = (eventData: IQuestions) => {
		const questionData = eventData;
		if (questionData.type === "answer") {
			addAnswers(questionData);
		}
		if (questionData.type === "question" && isCommentValidation) {
			addQuestions(questionData);
		}
		if (questionData.type === "question" && !isCommentValidation) {
			addQuestions(questionData);
			addApprovedQuestions(questionData);
		}
	};

	const handleEditQuestionEvent = (eventData: IQuestions) => {
		const questionData = eventData;
		editQuestion(questionData);
	};

	const handleChatEvent = (eventData: IMessage) => {
		const chatData = eventData;
		let firstName = "";
		let lastName = "";
		let subscriberFullName: string | undefined;
		let moderatorFullName: string | undefined;
		if (chatData.subscriber) {
			if (chatData.subscriber.liveSubscribers && chatData.subscriber.liveSubscribers.length === 1) {
				firstName = chatData.subscriber.liveSubscribers[0].name;
				lastName = chatData.subscriber.liveSubscribers[0].lastName;
			} else {
				firstName = chatData.subscriber.firstName;
				lastName = chatData.subscriber.lastName;
			}
			subscriberFullName = (chatData.subscriber as { fullName?: string }).fullName;
		} else {
			firstName = chatData?.moderator?.first_name;
			lastName = chatData?.moderator?.last_name;
			moderatorFullName = (chatData?.moderator as { fullName?: string })?.fullName;
		}
		const chatDataWithReply = {
			...chatData,
			...(chatData.subscriber && {
				subscriber: {
					...chatData.subscriber,
					firstName: firstName,
					lastName: lastName,
					...(subscriberFullName != null && { fullName: subscriberFullName }),
				},
			}),
			...(chatData?.moderator && {
				moderator: {
					...chatData?.moderator,
					...(moderatorFullName != null && { fullName: moderatorFullName }),
				},
			}),
			...(chatData?.replyMessageId && {
				repliedTo: chatData?.repliedTo,
			}),
		};
		addMessages(chatDataWithReply);
	};

	const handleReactionEvent = (eventData: IReactionEvent) => {
		const reactionData = eventData;
		addReaction(reactionData);
	};

	const handleApproveQuestionEvent = (eventData: IQuestions) => {
		const approvedQuestionData = eventData;
		addApprovedQuestions(approvedQuestionData);
		if (eventData.approved === false) {
			deleteQuestion(eventData);
		}
	};
	const handleDeleteQuestion = (eventData: IQuestions) => {
		const question = eventData;
		deleteQuestionByModerator(question);
	};

	const handleManageQuestion = (evenData: IQuestions) => {
		manageQuestion(evenData);
	};
	const handleLikeQuestion = (eventData: {
		data: {
			questionLike: IQuestionLike | null;
			result: string;
		};
		questionId: number;
	}) => {
		addQuestionLike(eventData);
	};

	const handleStartLiveEvent = () => {
		setLiveActive(1);
		setStatus(0);
		if (userToken && user?.role !== "moderator") {
			setParticipated.mutate();
		}
	};

	const handleEndLiveEvent = () => {
		setStatus(1);
		setLiveActive(0);
	};
	const handleLiveStreamEvent = (stream:number) => {
		setLiveStream(stream)
	}

	const handleBroadcastQuestion = (eventData: IQuestions) => {
		setBroadcastedQuestion(eventData);
	};
	const handleUserAlreadyConnected = async (e: any) => {
		const url = window.location.href;
		setUserSocketId(e.data.userId);
		setUserAlreadyConnected(true);
		if (user?.id && liveId) {
			sendEmailMutation.mutate({
				subscriberId: user?.id,
				liveId: liveId,
				url: url,
				ipAddress: userIpAddress,
			});
		}
	};

	useEffect(() => {
		let socket: WebSocket | null = null;
		let Mounted = true;
		let retryCount = 0;
		const join = async () => {
			const lru = new QuickLRU({ maxSize: 50 });
			// @ts-ignore
			let lastPingMs = null;
			let pingInterval: ReturnType<typeof setInterval> | undefined = undefined;

			if (liveId) {
				let wsUrl = window.process.env.WS_LIVE_URL;
				if (window.process.env.URL_LIVE_INTRANET === window.location.hostname) {
					wsUrl = "//" + window.process.env.URL_LIVE_INTRANET + "/live/ws";
				}
				if (!wsUrl?.includes("wss://")) {
					wsUrl = "wss:" + wsUrl;
				}
				wsUrl += "/?room=" + liveId;
				if (user?.role) {
					wsUrl += "&role=" + user?.role;
				} else {
					wsUrl += "&role=listener";
				}
				wsUrl += "&subscriberId=" + getOrCreateSubscriberId();

				const signedWsUrl = await generateSignedUrl(new URL(wsUrl));
				socket = new WebSocket(signedWsUrl);
				let rejoined = false;
				const rejoin = async () => {
					if (!rejoined) {
						rejoined = true;
						const delay = Math.min(1000 * Math.pow(2, retryCount), 30000);
						retryCount++;
						await new Promise((resolve) => setTimeout(resolve, delay));
						await join();
					}
				};
				// Handle socket events
				socket.onopen = () => {
					console.info(new Date(), "Socket connected");
					setWebSocketError(false);
					retryCount = 0;
					// Re-announce ourselves whenever we (re)connect, so the
					// livekit-client auditor list stays fresh after a reload,
					// network drop, or tab restore. The handler in
					// livekit-client deduplicates by fullName, so this is safe.
					try {
						const cachedName = (
							useStore.getState().listenerFullName || ""
						).trim();
						if (
							cachedName &&
							liveId != null &&
							socket?.readyState === WebSocket.OPEN
						) {
							socket.send(
								JSON.stringify({
									type: SocketEvents.participantJoined,
									data: {
										fullName: cachedName,
										liveId,
										joinedAt: new Date().toISOString(),
									},
								}),
							);
						}
					} catch (e) {
						console.error("Failed to re-announce participantJoined", e);
					}
					pingInterval = setInterval(() => {
						try {
							if (socket?.readyState === WebSocket.OPEN) {
								const id = crypto.randomUUID();
								lru.set(id, Date.now());
								// @ts-ignore
								socket.send(JSON.stringify({ type: "ping", data: { id, lastPingMs } }));
							} else {
								clearInterval(pingInterval!);
								if (Mounted) {
									rejoin();
								}
							}
						} catch (e) {
							clearInterval(pingInterval!);
							if (Mounted) {
								rejoin();
							}
						}
					}, 30000);
				};
				socket.onerror = (error) => {
					setWebSocketError(true);
					console.error("SOCKET ERROR: ", error);
					clearInterval(pingInterval!);
					if (socket?.readyState !== WebSocket.CLOSING && socket?.readyState !== WebSocket.CLOSED) {
						socket?.close();
						console.info(new Date(), "Socket closed");
					}
					if (Mounted) {
						rejoin();
					}
				};
				socket.onmessage = (event) => {
					try {
						const eventData: IEvent = JSON.parse(event.data);
						switch (eventData.type) {
							case SocketEvents.question: {
								handleQuestionEvent(eventData.data as IQuestions);
								break;
							}
							case SocketEvents.chat: {
								handleChatEvent(eventData.data as IMessage);
								break;
							}
							case SocketEvents.reaction: {
								handleReactionEvent(eventData.data as IReactionEvent);
								break;
							}
							case SocketEvents.approveQuestion: {
								handleApproveQuestionEvent(eventData.data as IQuestions);
								break;
							}
							case SocketEvents.editQuestion: {
								handleEditQuestionEvent(eventData.data as IQuestions);
								break;
							}
							case SocketEvents.deleteQuestion: {
								handleDeleteQuestion(eventData.data as IQuestions);
								break;
							}
							case SocketEvents.manageQuestion: {
								handleManageQuestion(eventData.data as IQuestions);
								break;
							}
							case SocketEvents.likeQuestion: {
								handleLikeQuestion(
									eventData.data as {
										data: {
											questionLike: IQuestionLike | null;
											result: string;
										};
										questionId: number;
									}
								);
								break;
							}
							case SocketEvents.liveStarted: {
								handleStartLiveEvent();
								break;
							}
							case SocketEvents.liveEnded: {
								handleEndLiveEvent();
								break;
							}
							case SocketEvents.liveStream: {
								const stream = eventData.data as IStream;
								handleLiveStreamEvent(stream.status);
								break;
							}
							case SocketEvents.broadcastQuestion: {
								handleBroadcastQuestion(eventData.data as IQuestions);
								break;
							}
							case SocketEvents.userAlreadyConnected: {
								handleUserAlreadyConnected(eventData);
								break;
							}
							case SocketEvents.userVerified: {
								try {
									socket?.close();
									navigate(`/${liveToken}`);
								} catch (e) {}
								break;
							}
							case SocketEvents.pollPublished: {
								addPublicPoll(eventData.data as IPoll);
								break;
							}
						case SocketEvents.pollUnpublished: {
							const unpublishedData = eventData.data as { pollId: number };
							removePublicPoll(unpublishedData.pollId);
							break;
						}
						case SocketEvents.pollDeleted: {
							const deletedData = eventData.data as { pollId: number };
							removePublicPoll(deletedData.pollId);
							break;
						}
						case SocketEvents.pollUpdated: {
							updatePublicPoll(eventData.data as IPoll);
							break;
						}
							case SocketEvents.pollVote: {
								updatePollVote(eventData.data as IPollVoteEvent);
								break;
							}
							case SocketEvents.documentPublished: {
								const raw = eventData.data as { file?: ILiveFile; liveId?: number };
								if (raw?.file) {
									useStore.getState().upsertPublicLiveFile(raw.file);
									if (useStore.getState().activeTab !== 10) {
										toast.success(i18n.t("live_page.new_document"));
									}
								}
								break;
							}
							case SocketEvents.documentUnpublished: {
								const raw = eventData.data as { fileId?: number; liveId?: number };
								if (raw?.fileId != null) {
									useStore.getState().removePublicLiveFile(raw.fileId);
								}
								break;
							}
							case SocketEvents.documentDeleted: {
								const raw = eventData.data as { fileId?: number; liveId?: number };
								if (raw?.fileId != null) {
									useStore.getState().removePublicLiveFile(raw.fileId);
								}
								break;
							}
							case SocketEvents.requestAuditorRoster: {
								// An admin/livekit-client just connected and is asking
								// every auditor in this room to announce themselves so
								// it can build the auditor list. Reply only when we
								// know our name and we're on the same live.
								try {
									const data = (eventData.data || {}) as { liveId?: number };
									if (
										data?.liveId != null &&
										liveId != null &&
										Number(data.liveId) !== Number(liveId)
									) {
										break;
									}
									const cachedName = (
										useStore.getState().listenerFullName || ""
									).trim();
									if (
										cachedName &&
										liveId != null &&
										socket?.readyState === WebSocket.OPEN
									) {
										socket.send(
											JSON.stringify({
												type: SocketEvents.participantJoined,
												data: {
													fullName: cachedName,
													liveId,
													joinedAt: new Date().toISOString(),
												},
											}),
										);
									}
								} catch (e) {
									console.error("Failed to answer auditor roster request", e);
								}
								break;
							}
							case SocketEvents.inviteAuditor: {
								const invitation = eventData.data as IAuditorInvitation;
								if (!invitation || !invitation.fullName) break;
								// Only show the notification to the targeted auditor.
								// Use latest store state so we don't capture a stale name.
								const currentName = (
									useStore.getState().listenerFullName || ""
								)
									.trim()
									.toLowerCase();
								const targetName = (invitation.fullName || "").trim().toLowerCase();
								if (!currentName || currentName !== targetName) break;
								// Optional: scope to the same live id when available.
								if (
									invitation.liveId != null &&
									liveId != null &&
									Number(invitation.liveId) !== Number(liveId)
								) {
									break;
								}
								setAuditorInvitation(invitation);
								break;
							}
							case SocketEvents.liveFilesRefresh: {
								if (liveId) {
									fetchPublicLiveFiles(liveId)
										.then((files) => {
											const st = useStore.getState();
											if (st.activeTab !== 10) {
												toast.success(i18n.t("live_page.documents_updated"));
												st.setDocumentsNotifications(
													st.documentsNotifications + 1,
												);
											}
											st.setPublicLiveFiles(files);
										})
										.catch((e) => {
											console.error("live_files_refresh", e);
										});
								}
								break;
							}
							case SocketEvents.error: {
								console.info(new Date(), "ERROR:", eventData.data);
								rejoin();
								break;
							}
							case SocketEvents.pong: {
								const data = eventData.data as IPong;
								const ping = lru.get(data.id);
								if (ping) {
									// @ts-ignore
									lastPingMs = Date.now() - ping;
								}
								break;
							}

							default:
								break;
						}
					} catch (e) {
						console.error("Error parsing socket message", e);
						socket?.send(JSON.stringify({ type: "error", data: "Error parsing socket message" }));
					}
				};

				socket.onclose = () => {
					setWebSocketError(true);
					clearInterval(pingInterval!);
					console.info(new Date(), "Socket disconnected");
					if (Mounted) {
						rejoin();
					}
				};

				// Set the new socket in  state
				setWebSocket(socket);
			}
		};
		join();

		// Best-effort "participantLeft" notification so livekit-client can
		// drop stale auditors when someone closes the tab / navigates away.
		const sendParticipantLeft = () => {
			try {
				const cachedName = (useStore.getState().listenerFullName || "").trim();
				if (
					cachedName &&
					liveId != null &&
					socket?.readyState === WebSocket.OPEN
				) {
					socket.send(
						JSON.stringify({
							type: SocketEvents.participantLeft,
							data: {
								fullName: cachedName,
								liveId,
								leftAt: new Date().toISOString(),
							},
						}),
					);
				}
			} catch {
				/* best-effort; nothing else we can do here */
			}
		};
		const onPageHide = () => sendParticipantLeft();
		window.addEventListener("pagehide", onPageHide);
		window.addEventListener("beforeunload", onPageHide);

		// Cleanup function
		return () => {
			Mounted = false;
			window.removeEventListener("pagehide", onPageHide);
			window.removeEventListener("beforeunload", onPageHide);
			sendParticipantLeft();
			if (socket?.readyState !== WebSocket.CLOSING && socket?.readyState !== WebSocket.CLOSED) {
				socket?.close();
				console.info(new Date(), "Socket closed");
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [liveId]);
};

export default useSocket;
